import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { User, Bell, HelpCircle, LogOut, Camera, Trophy, Building2, Users, AlertTriangle, CheckCircle, CreditCard, XCircle, X, UserPlus, ClipboardList } from "lucide-react";
import AVModal from "@/components/AVModal";
import EntityTypeahead from "@/components/EntityTypeahead";
import { useDynamicPricing, labelForPlanId, whenReady, planIdForCheckout } from "@/lib/subscriptionPricing";
import { checkoutErrorMessage } from "@/components/StripeCheckoutModal";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Roles that can ask to join a team or add another one. Mirrors the backend's
// TEAM_REQUEST_ROLES. League/YSV admins are excluded because they already see
// every team in their scope; GDOs get teams via assignment instead.
const TEAM_REQUEST_ROLES = ["player_parent", "coach", "amateur", "org_admin"];

// Roles the backend can put behind the paywall — `_pg_premium_access_reason`
// falls through to `must_subscribe` for all of them. They need this card both
// to buy and, once paying, to reach the Stripe portal: it is the app's ONLY
// link to /payments/portal, so a role left out here can be charged but can
// never update a card or cancel without emailing support.
//
// coach and org_admin also get access free once their team/org passes a member
// threshold, so the card asks /subscription/status before offering to sell them
// anything (see SubscriptionSection).
const SUBSCRIBER_ROLES = ["player_parent", "coach", "amateur", "pro_league_admin", "org_admin"];

const ProfilePage = () => {
  const { user, logout, api, setUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [showAV, setShowAV] = useState(false);
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessRequests, setAccessRequests] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const fileInputRef = useRef(null);

  const loadProfile = () => {
    api().get("/profile/full").then(r => setProfile(r.data)).catch(() => {});
  };

  const loadAccessRequests = () => {
    api().get("/team-access-requests").then(r => setAccessRequests(r.data || [])).catch(() => setAccessRequests([]));
  };

  useEffect(() => { loadProfile(); }, [api]);
  useEffect(() => {
    // Every member role can request multi-team access; load their history.
    if (TEAM_REQUEST_ROLES.includes(user?.role)) loadAccessRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, api]);

  const handleLogout = () => { logout(); navigate("/login"); };

  const onPickPhoto = () => {
    setPhotoError(null);
    fileInputRef.current?.click();
  };

  const onPhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setPhotoError("Please choose an image file");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setPhotoError("Image must be under 3 MB");
      return;
    }
    setUploadingPhoto(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const r = await api().put("/users/profile", { profile_photo: dataUrl });
      setUser(r.data);
    } catch (err) {
      setPhotoError(err.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <div data-testid="profile-page" style={{ padding: '32px 40px', maxWidth: 600, margin: '0 auto' }}>
      {/* Avatar */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: 12 }}>
          <button
            data-testid="profile-photo-btn"
            type="button"
            onClick={onPickPhoto}
            disabled={uploadingPhoto}
            aria-label="Change profile photo"
            style={{
              padding: 0, border: 'none', background: 'transparent',
              cursor: uploadingPhoto ? 'wait' : 'pointer', position: 'relative',
              width: 96, height: 96, borderRadius: '50%', overflow: 'hidden',
            }}
          >
            {user?.profile_photo ? (
              <img src={user.profile_photo} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: '#ff6b00', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, fontWeight: 700, color: '#fff' }}>{user?.name?.charAt(0) || 'U'}</div>
            )}
            {uploadingPhoto && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 600 }}>Uploading…</div>
            )}
          </button>
          <button
            type="button"
            data-testid="profile-photo-camera-btn"
            onClick={onPickPhoto}
            disabled={uploadingPhoto}
            aria-label="Change profile photo"
            style={{
              position: 'absolute', bottom: 0, right: 0, width: 32, height: 32, borderRadius: '50%',
              background: 'var(--cyan)', border: '2px solid var(--dark-card)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: uploadingPhoto ? 'wait' : 'pointer', padding: 0,
            }}
          >
            <Camera size={14} style={{ color: '#000' }} />
          </button>
          <input
            ref={fileInputRef}
            data-testid="profile-photo-input"
            type="file"
            accept="image/*"
            onChange={onPhotoSelected}
            style={{ display: 'none' }}
          />
        </div>
        {photoError && <p data-testid="photo-error" style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>{photoError}</p>}
        <h1 data-testid="profile-name" style={{ fontFamily: "'Outfit', sans-serif", fontSize: 24, fontWeight: 700, marginTop: 8 }}>{user?.name}</h1>
        <p data-testid="profile-email" style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{user?.email}</p>
        <span style={{ display: 'inline-block', marginTop: 8, background: 'rgba(255,107,0,0.15)', color: '#ff6b00', fontSize: 12, fontWeight: 600, padding: '4px 14px', borderRadius: 20 }}>{user?.role?.replace('_', ' ').toUpperCase()}</span>
      </div>

      {/* Associated Structure */}
      {profile && (profile.league_name || profile.org_name || profile.team_name) && (
        <div data-testid="profile-associations" className="glass-card" style={{ padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--text-secondary)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Associations</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {profile.league_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Trophy size={16} style={{ color: 'var(--cyan)', flexShrink: 0 }} />
                <div><p style={{ fontSize: 12, color: '#888' }}>League</p><p data-testid="profile-league" style={{ fontSize: 14, fontWeight: 600 }}>{profile.league_name}</p></div>
              </div>
            )}
            {profile.org_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Building2 size={16} style={{ color: '#9c27b0', flexShrink: 0 }} />
                <div><p style={{ fontSize: 12, color: '#888' }}>Organization</p><p data-testid="profile-org" style={{ fontSize: 14, fontWeight: 600 }}>{profile.org_name}</p></div>
              </div>
            )}
            {profile.team_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Users size={16} style={{ color: '#4caf50', flexShrink: 0 }} />
                <div><p style={{ fontSize: 12, color: '#888' }}>Team</p><p data-testid="profile-team" style={{ fontSize: 14, fontWeight: 600 }}>{profile.team_name}</p></div>
              </div>
            )}
            {/* Team-less player/coach — show Request to Join CTA */}
            {!profile.team_name && TEAM_REQUEST_ROLES.includes(user?.role) && (
              <button
                data-testid="profile-request-join-btn"
                onClick={() => setShowJoinModal(true)}
                style={{
                  width: '100%', marginTop: 4, padding: '12px 16px',
                  background: 'linear-gradient(135deg, rgba(0,229,255,0.12), rgba(76,175,80,0.10))',
                  border: '1px solid rgba(0,229,255,0.35)', borderRadius: 12,
                  color: '#fff', cursor: 'pointer', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 12,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                <UserPlus size={20} style={{ color: 'var(--cyan)', flexShrink: 0 }} />
                <div>
                  <p style={{ fontSize: 14, fontWeight: 700 }}>Request to Join an Org / Team</p>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>You're not on a team right now — pick one and we'll send your request to their coach/admin.</p>
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Multi-Team Affiliations — Coach & Org Admin */}
      {TEAM_REQUEST_ROLES.includes(user?.role) && (
        <div data-testid="profile-multi-team" className="glass-card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Other Teams</h3>
            <button
              data-testid="profile-request-team-access-btn"
              onClick={() => setShowAccessModal(true)}
              style={{
                background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)',
                borderRadius: 8, color: 'var(--cyan)', padding: '6px 12px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <UserPlus size={13} /> Request Access
            </button>
          </div>
          {(profile?.additional_teams || []).length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>You haven't been added to any other teams yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {profile.additional_teams.map(t => (
                <div key={t.id} data-testid={`extra-team-${t.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(76,175,80,0.06)', border: '1px solid rgba(76,175,80,0.2)' }}>
                  <Users size={14} style={{ color: '#4caf50' }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</p>
                    {t.org_name && <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t.org_name}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {accessRequests.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Recent Requests</p>
              {accessRequests.slice(0, 5).map(r => {
                const color = r.status === 'approved' ? '#4caf50' : r.status === 'rejected' ? '#f44336' : '#ff9800';
                return (
                  <div key={r.id} data-testid={`access-request-${r.id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.02)', marginBottom: 4 }}>
                    <p style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 600 }}>{r.team_name || 'Team'}</span>
                      {r.target_org_name && <span style={{ color: 'var(--text-secondary)' }}> · {r.target_org_name}</span>}
                    </p>
                    <span style={{ fontSize: 10, fontWeight: 700, color, padding: '2px 8px', borderRadius: 6, background: `${color}15` }}>{(r.status || 'pending').toUpperCase()}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* AV Status */}
      {profile?.av_enabled && (user?.role === "player_parent" || user?.role === "coach") && (
        <div data-testid="profile-av-section" className="glass-card" style={{ padding: 20, marginBottom: 20 }}>
          {user?.av_status === "verified" ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <CheckCircle size={22} style={{ color: '#4caf50' }} />
              <div>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#4caf50' }}>Age Verification Complete</p>
                <p style={{ fontSize: 12, color: '#888' }}>DOB: {user?.player_dob} {user?.jersey_number ? `| #${user.jersey_number}` : ''}</p>
              </div>
            </div>
          ) : (
            <button data-testid="profile-av-btn" onClick={() => setShowAV(true)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px',
              background: 'rgba(255,152,0,0.1)', border: '2px solid rgba(255,152,0,0.3)',
              borderRadius: 10, cursor: 'pointer', textAlign: 'left',
            }}>
              <AlertTriangle size={22} style={{ color: '#ff9800', flexShrink: 0 }} />
              <div>
                <p style={{ fontSize: 14, fontWeight: 700, color: '#ff9800' }}>Complete Age Verification</p>
                <p style={{ fontSize: 12, color: '#cc8a00' }}>Required by your league</p>
              </div>
            </button>
          )}
        </div>
      )}
      {showAV && <AVModal onClose={() => setShowAV(false)} onSuccess={loadProfile} />}

      {/* Subscription Status */}
      {SUBSCRIBER_ROLES.includes(user?.role) && (
        <SubscriptionSection user={user} />
      )}

      {/* Settings */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Account</h2>
      {["ysv_admin", "league_coordinator", "hub_coordinator", "gdo_coordinator"].includes(user?.role) && (
        <SettingsRow
          testId="profile-option-ysv-logistics"
          icon={ClipboardList}
          label="YSV Logistics"
          onClick={() => navigate('/ysv-logistics')}
        />
      )}
      <SettingsRow testId="profile-option-change-profile-photo" icon={User} label="Change Profile Photo" onClick={onPickPhoto} />
      <SettingsRow testId="profile-option-notifications" icon={Bell} label="Notifications" onClick={() => setShowNotifModal(true)} />
      <SettingsRow testId="profile-option-help-support" icon={HelpCircle} label="Help & Support" onClick={() => navigate('/support')} />

      {showNotifModal && <NotificationsModal api={api} onClose={() => setShowNotifModal(false)} />}
      {showJoinModal && <JoinTeamModal api={api} onClose={() => setShowJoinModal(false)} onSuccess={loadProfile} />}
      {showAccessModal && <RequestTeamAccessModal api={api} user={user} onClose={() => setShowAccessModal(false)} onSuccess={() => { loadAccessRequests(); loadProfile(); }} />}

      <button data-testid="logout-btn" onClick={handleLogout} style={{ width: '100%', padding: '14px', marginTop: 24, background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.2)', borderRadius: 12, color: '#f44336', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: "'DM Sans', sans-serif" }}><LogOut size={18} /> Logout</button>
      <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12, marginTop: 24 }}>YSV Youth Sports Video v1.0.0</p>
    </div>
  );
};

const SettingsRow = ({ testId, icon: Icon, label, onClick }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="glass-card"
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '16px 20px', marginBottom: 8, cursor: 'pointer',
      width: '100%', border: 'none', textAlign: 'left', color: 'inherit',
      fontFamily: 'inherit',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <Icon size={18} style={{ color: 'var(--text-secondary)' }} />
      <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
    </div>
    <span style={{ color: 'var(--text-secondary)' }}>&gt;</span>
  </button>
);

const NOTIF_KEYS = [
  { key: 'games_uploaded', label: 'Games Uploaded', desc: 'Alerts when YSV uploads a new game video for your team' },
  { key: 'schedule_updates', label: 'Schedule Updates', desc: 'Changes to game times, locations, or cancellations' },
  { key: 'messages', label: 'Messages', desc: 'In-app messages from coaches, admins, or teammates' },
  { key: 'subscription_updates', label: 'Subscription Updates', desc: 'Billing, renewals, and payment reminders' },
];

const NotificationsModal = ({ api, onClose }) => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api().get('/users/notification-prefs')
      .then(r => setPrefs(r.data))
      .catch(() => setPrefs({ games_uploaded: true, schedule_updates: true, messages: true, subscription_updates: true }));
  }, [api]);

  const toggle = async (key) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true); setError(null);
    try {
      const r = await api().put('/users/notification-prefs', { [key]: next[key] });
      setPrefs(r.data);
    } catch (err) {
      setError('Failed to save');
      setPrefs(prefs); // revert
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div data-testid="notifications-modal" onClick={e => e.stopPropagation()} style={{
        background: '#1a2230', border: '1px solid #253040', borderRadius: 16,
        padding: 28, width: '100%', maxWidth: 460,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <h3 style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>App Notifications</h3>
          <button data-testid="notifications-close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: 4 }}>
            <X size={20} />
          </button>
        </div>
        <p style={{ color: '#888', fontSize: 13, marginBottom: 20 }}>Choose what you want to be notified about in the app.</p>

        {!prefs ? (
          <p style={{ color: '#888', fontSize: 13 }}>Loading…</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {NOTIF_KEYS.map(({ key, label, desc }) => (
              <button
                key={key}
                type="button"
                data-testid={`notif-toggle-${key}`}
                onClick={() => toggle(key)}
                disabled={saving}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px', background: '#0f141d', border: '1px solid #253040',
                  borderRadius: 10, cursor: saving ? 'wait' : 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ flex: 1, paddingRight: 12 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 2 }}>{label}</p>
                  <p style={{ fontSize: 12, color: '#888' }}>{desc}</p>
                </div>
                <Toggle on={!!prefs[key]} />
              </button>
            ))}
          </div>
        )}

        {error && <p style={{ color: '#f44336', fontSize: 12, marginTop: 12 }}>{error}</p>}
      </div>
    </div>
  );
};

const Toggle = ({ on }) => (
  <span style={{
    display: 'inline-block', width: 44, height: 24, borderRadius: 999,
    background: on ? 'var(--cyan)' : '#2a3342', position: 'relative',
    transition: 'background 0.2s', flexShrink: 0,
  }}>
    <span style={{
      position: 'absolute', top: 2, left: on ? 22 : 2, width: 20, height: 20,
      borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
    }} />
  </span>
);

const SubscriptionSection = ({ user }) => {
  const [subscribing, setSubscribing] = useState(false);
  const [managing, setManaging] = useState(false);
  const isActive = user?.subscription_status === "active";
  const isPastDue = user?.subscription_status === "past_due";
  // Current monthly Fan/Member catalog, priced from Stripe.
  const pricing = useDynamicPricing();
  const { api } = useAuth() || {};
  // Held back until the plans call settles — `pricing` is the youth default
  // before then, so an amateur would briefly read the youth price.
  const planLabel = whenReady(pricing, labelForPlanId(pricing, user?.subscription_plan_id));

  // `user.subscription_status` only knows about PAID access. Coaches and org
  // admins also get access free once their team/org passes a member threshold,
  // and without asking the backend this card told them "No Active Subscription"
  // and sold them something they already have. Same source the paywall uses.
  const [access, setAccess] = useState(null);
  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    api().get("/subscription/status")
      .then(r => { if (!cancelled) setAccess(r.data); })
      .catch(() => { if (!cancelled) setAccess({ reason: "unknown" }); });
    return () => { cancelled = true; };
  }, [api]);

  const THRESHOLD_REASONS = ["org_size_threshold", "team_size_threshold"];
  const isThreshold = THRESHOLD_REASONS.includes(access?.reason);
  const freeByThreshold = isThreshold && access?.has_access;
  const scope = access?.reason === "org_size_threshold" ? "organization" : "team";

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      // Resolve at click time when the cached pricing didn't come from the
      // backend — otherwise a failed plans fetch sends an amateur to checkout
      // with the youth plan id, which the tier gate rejects.
      const client = api();
      const plan_id = await planIdForCheckout(client, pricing, "member");
      const r = await client.post("/payments/checkout", { plan_id, origin_url: window.location.origin });
      if (r.data.url) window.location.href = r.data.url;
    } catch (err) {
      alert(checkoutErrorMessage(err));
    }
    setSubscribing(false);
  };

  const openBillingPortal = async () => {
    const token = localStorage.getItem("ysv_token");
    setManaging(true);
    try {
      const r = await axios.post(`${API_URL}/api/payments/portal`, { return_url: `${window.location.origin}/profile` }, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data.url) window.location.href = r.data.url;
    } catch (err) {
      alert(err.response?.data?.detail || "Could not open billing portal");
    }
    setManaging(false);
  };

  return (
    <div data-testid="profile-subscription" className="glass-card" style={{ padding: 20, marginBottom: 20 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--text-secondary)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Subscription</h3>
      {isActive ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(76,175,80,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CreditCard size={20} style={{ color: '#4caf50' }} />
            </div>
            <div>
              <p data-testid="sub-status-active" style={{ fontSize: 15, fontWeight: 700, color: '#4caf50' }}>{user?.subscription_tier === "fan" ? "Fan Account" : "YSV Member"} - Active</p>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{planLabel} &bull; {user?.subscription_tier === "fan" ? "Access to YSVLive" : "Full access to games, clips & highlights"}</p>
            </div>
          </div>
          <button data-testid="profile-manage-sub-btn" onClick={openBillingPortal} disabled={managing} style={{
            width: '100%', padding: '12px', background: 'transparent',
            border: '1px solid rgba(0,229,255,0.4)', borderRadius: 10, color: 'var(--cyan)',
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8, opacity: managing ? 0.6 : 1,
          }}>
            <CreditCard size={14} /> {managing ? 'Opening…' : 'Manage Subscription'}
          </button>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 8 }}>
            Update card, switch plans, or cancel — all in Stripe's secure portal.
          </p>
        </>
      ) : isPastDue ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,152,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CreditCard size={20} style={{ color: '#ff9800' }} />
            </div>
            <div>
              <p data-testid="sub-status-past-due" style={{ fontSize: 15, fontWeight: 700, color: '#ff9800' }}>Payment Failed</p>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Update your payment method to keep your YSV access.</p>
            </div>
          </div>
          <button data-testid="profile-update-card-btn" onClick={openBillingPortal} disabled={managing} style={{
            width: '100%', padding: '14px', background: '#ff9800', border: 'none',
            borderRadius: 10, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            fontFamily: "'Outfit', sans-serif", display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8, opacity: managing ? 0.7 : 1,
          }}>
            <CreditCard size={16} /> {managing ? 'Opening…' : 'Update Payment Method'}
          </button>
        </>
      ) : access === null ? (
        // Don't guess before the backend answers — "No Active Subscription" is
        // wrong for anyone whose access is free by threshold.
        <p data-testid="sub-status-loading" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Checking your access…</p>
      ) : freeByThreshold ? (
        <div data-testid="sub-status-included" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(76,175,80,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CheckCircle size={20} style={{ color: '#4caf50' }} />
          </div>
          <div>
            <p style={{ fontSize: 15, fontWeight: 700, color: '#4caf50' }}>Included with your {scope}</p>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {access.count} of {access.threshold} approved members — your access stays free while your {scope} is at or above {access.threshold}.
            </p>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(244,67,54,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <XCircle size={20} style={{ color: '#f44336' }} />
            </div>
            <div>
              <p data-testid="sub-status-inactive" style={{ fontSize: 15, fontWeight: 700, color: '#f44336' }}>No Active Subscription</p>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Subscribe to access games, clips & highlights</p>
            </div>
          </div>
          {/* Below the free threshold: say how close they are, so paying reads as
              a choice rather than the only option. Mirrors the paywall's copy. */}
          {isThreshold && (
            <p data-testid="sub-threshold-hint" style={{ fontSize: 12, color: 'var(--cyan)', marginBottom: 12 }}>
              Your {scope} unlocks free access at {access.threshold} approved members — you're {access.remaining} away.
            </p>
          )}
          <div>
            <button data-testid="profile-subscribe-monthly-btn" onClick={handleSubscribe} disabled={subscribing || !pricing.ready} style={{
              padding: '14px 10px', background: '#ff6b00', border: 'none',
              borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', width: '100%',
              fontFamily: "'Outfit', sans-serif", display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 2, opacity: (subscribing || !pricing.ready) ? 0.7 : 1,
            }}>
              <span style={{ fontSize: 11, opacity: 0.85 }}>YSV Member · Monthly</span>
              <span style={{ fontSize: 15 }}>{whenReady(pricing, `${pricing.member_display}/mo`)}</span>
            </button>
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 10 }}>
            Have a promo code? Enter it at checkout. Cancel anytime.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;

/* ═══ Join Team Modal ═══ */
const JoinTeamModal = ({ api, onClose, onSuccess }) => {
  const [leagues, setLeagues] = React.useState([]);
  const [leagueId, setLeagueId] = React.useState("");
  const [teamId, setTeamId] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    axios.get(`${API_URL}/api/public/leagues`).then(r => setLeagues(r.data || [])).catch(() => {});
  }, []);

  const submit = async () => {
    setError(null);
    if (!teamId) { setError("Pick a team"); return; }
    setSubmitting(true);
    try {
      await api().post("/users/me/join-request", { team_id: teamId });
      setDone(true);
      if (onSuccess) onSuccess();
      setTimeout(() => { onClose(); }, 1400);
    } catch (err) {
      setError(err.response?.data?.detail || "Request failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="join-team-modal" onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: 16,
    }}>
      <div className="glass-card" style={{ padding: 24, width: 'min(96vw, 520px)', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Outfit', sans-serif" }}>Request to Join a Team</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={20} />
          </button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 18 }}>Pick the league and team you want to join. Your request will be sent to the team's coach and the org admin for approval.</p>

        {done ? (
          <div data-testid="join-team-success" style={{ padding: 24, textAlign: 'center', color: '#4caf50' }}>
            <CheckCircle size={44} style={{ marginBottom: 10 }} />
            <p style={{ fontSize: 15, fontWeight: 700 }}>Request sent!</p>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>The team's coach / org admin will review your request.</p>
          </div>
        ) : (
          <>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>League
              <select data-testid="join-league-select" className="input-field" value={leagueId} onChange={e => { setLeagueId(e.target.value); setTeamId(""); }}>
                <option value="">— Select a league —</option>
                {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>

            {leagueId && (
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 12, display: 'block' }}>Team
                {/* Server-side search: the old version intersected two capped
                    client-side lists, so teams past the cap were unreachable. */}
                <EntityTypeahead
                  endpoint="/public/teams"
                  params={{ league_id: leagueId }}
                  value={teamId}
                  onChange={(id) => setTeamId(id)}
                  placeholder="Type a team or club name…"
                  emptyLabel="No teams in this league"
                  subtitleFor={(t) => t.org_name}
                  testid="join-team-picker"
                />
              </label>
            )}

            {error && <p data-testid="join-team-error" style={{ color: '#f44336', fontSize: 13, marginTop: 10 }}>{error}</p>}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={onClose} disabled={submitting} style={{
                flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--dark-border)',
                borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer',
              }}>Cancel</button>
              <button data-testid="join-team-submit" onClick={submit} disabled={!teamId || submitting} className="btn-primary" style={{ flex: 2, opacity: (!teamId || submitting) ? 0.5 : 1 }}>
                {submitting ? "Sending…" : "Submit request"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ═══ Multi-Team Access Modal — Coach / Org Admin requests view+manage access to other teams ═══ */
const RequestTeamAccessModal = ({ api, user, onClose, onSuccess }) => {
  const [leagues, setLeagues] = React.useState([]);
  const [leagueId, setLeagueId] = React.useState("");
  const [orgId, setOrgId] = React.useState("");
  const [teamId, setTeamId] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [error, setError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    axios.get(`${API_URL}/api/public/leagues`).then(r => setLeagues(r.data || [])).catch(() => {});
  }, []);

  // Hide the user's primary team and any team they already have access to.
  const isPickable = React.useCallback(
    (t) => t.id !== user?.team_id && !(user?.additional_team_ids || []).includes(t.id),
    [user?.team_id, user?.additional_team_ids],
  );

  const submit = async () => {
    setError(null);
    if (!teamId) { setError("Pick a team to request access to"); return; }
    setSubmitting(true);
    try {
      await api().post("/users/me/team-access-request", { team_id: teamId, reason });
      setDone(true);
      if (onSuccess) onSuccess();
      setTimeout(() => onClose(), 1400);
    } catch (err) {
      setError(err.response?.data?.detail || "Request failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="request-team-access-modal" onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: 16,
    }}>
      <div className="glass-card" style={{ padding: 24, width: 'min(96vw, 520px)', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Outfit', sans-serif" }}>Request Team Access</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={20} />
          </button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 18 }}>Pick the team you want to add to your account. The target organization's admin will review your request.</p>

        {done ? (
          <div data-testid="request-team-access-success" style={{ padding: 24, textAlign: 'center', color: '#4caf50' }}>
            <CheckCircle size={44} style={{ marginBottom: 10 }} />
            <p style={{ fontSize: 15, fontWeight: 700 }}>Request sent!</p>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>The target org admin will review your request.</p>
          </div>
        ) : (
          <>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>League
              <select data-testid="access-league-select" className="input-field" value={leagueId} onChange={e => { setLeagueId(e.target.value); setOrgId(""); setTeamId(""); }}>
                <option value="">— Select a league —</option>
                {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>

            {leagueId && (
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 12, display: 'block' }}>Organization
                {/* This used to filter on `o.league_id`, a field the public org
                    projection stopped returning after the remodel — so the list
                    was always empty ("does not show any orgs. or teams"). The
                    league filter is now applied server-side. */}
                <EntityTypeahead
                  endpoint="/public/organizations"
                  params={{ league_id: leagueId }}
                  value={orgId}
                  onChange={(id) => { setOrgId(id); setTeamId(""); }}
                  placeholder="Type an organization name…"
                  emptyLabel="No organizations in this league"
                  testid="access-org-picker"
                />
              </label>
            )}

            {orgId && (
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 12, display: 'block' }}>Team
                <EntityTypeahead
                  endpoint="/public/teams"
                  params={{ org_id: orgId }}
                  value={teamId}
                  onChange={(id) => setTeamId(id)}
                  placeholder="Type a team name…"
                  emptyLabel="No additional teams in this org"
                  filterItem={isPickable}
                  testid="access-team-picker"
                />
              </label>
            )}

            {teamId && (
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 12, display: 'block' }}>Reason (optional)
                <textarea
                  data-testid="access-reason"
                  className="input-field"
                  rows={3}
                  maxLength={500}
                  placeholder="Why do you need access? (e.g., new co-coach assignment)"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  style={{ resize: 'vertical' }}
                />
              </label>
            )}

            {error && <p data-testid="access-error" style={{ color: '#f44336', fontSize: 13, marginTop: 10 }}>{error}</p>}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={onClose} disabled={submitting} style={{
                flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--dark-border)',
                borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer',
              }}>Cancel</button>
              <button data-testid="access-submit" onClick={submit} disabled={!teamId || submitting} className="btn-primary" style={{ flex: 2, opacity: (!teamId || submitting) ? 0.5 : 1 }}>
                {submitting ? "Sending…" : "Submit Request"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
